import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { LeadTasksSection } from "./LeadTasksSection";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  UserPlus,
  Edit,
  Trash2,
  RefreshCw,
  Search,
  AlertTriangle,
  ArrowRightCircle,
  Building,
  Mail,
  Phone,
  UserCheck,
  Ban,
  CheckCircle,
  Clock,
  Briefcase,
  IndianRupee,
  Flame,
  Zap,
  Snowflake,
  Inbox,
  User,
  Users,
  Calendar,
  History,
  Tag,
  Globe,
  MapPin,
  HelpCircle,
  Share2,
  Upload,
  FileSpreadsheet,
  ChevronDown,
} from "lucide-react";
import { formatINR, cn } from "@/lib/utils";
import { parseCsvToObjects } from "@/lib/csv";
import type { LeadRecord, LeadStatus, LeadPriority, MasterLookupRecord } from "@/types";
import { FilterBar } from "@/components/filters/FilterBar";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, valueOptionsFromFacets, type OwnerFacet } from "@/lib/filters";
import {
  CalendarDays as FilterDateIcon,
  Flame as FilterPriorityIcon,
  MapPin as FilterStateIcon,
  Megaphone as FilterSourceIcon,
  UserCircle2 as FilterOwnerIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const LEAD_FILTER_DEFAULTS = { owner: "all", priority: "all", source: "all", state: "all", created: "any" };
const PRIORITY_OPTIONS = [
  { value: "all", label: "All priorities" },
  { value: "HOT", label: "Hot" },
  { value: "WARM", label: "Warm" },
  { value: "COLD", label: "Cold" },
];

const STATUS_CONFIG: Record<
  LeadStatus,
  { label: string; bg: string; text: string }
> = {
  NEW: { label: "New", bg: "bg-blue-500/10 border-blue-500/20", text: "text-blue-600 dark:text-blue-400" },
  CONTACTED: { label: "Contacted", bg: "bg-amber-500/10 border-amber-500/20", text: "text-amber-600 dark:text-amber-400" },
  QUALIFIED: { label: "Qualified", bg: "bg-purple-500/10 border-purple-500/20", text: "text-purple-600 dark:text-purple-400" },
  CONVERTED: { label: "Converted", bg: "bg-emerald-500/10 border-emerald-500/20", text: "text-emerald-600 dark:text-emerald-400" },
  DISQUALIFIED: { label: "Disqualified", bg: "bg-destructive/10 border-destructive/20", text: "text-destructive" },
};

/** CSV columns accepted by the bulk import (header names are case-insensitive). */
const IMPORT_TEXT_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "mobile",
  "alternate_phone",
  "job_title",
  "company_name",
  "industry",
  "company_size",
  "website",
  "city",
  "state",
  "region",
  "source",
  "campaign",
  "referred_by",
  "product_interest",
  "expected_timeline",
  "requirement_notes",
  "priority",
] as const;
const IMPORT_NUMBER_FIELDS = ["expected_budget", "estimated_value"] as const;
const IMPORT_TEMPLATE_HEADER = [...IMPORT_TEXT_FIELDS, ...IMPORT_NUMBER_FIELDS, "tags"].join(",");

interface ImportResult {
  successCount: number;
  total: number;
  errors: Array<{ row: number; reason: string }>;
}

export function LeadsPage() {
  const { hasPermission, user, getScope } = useAuth();
  // Sales Executives (scope "own") have no team, so the Team tab is hidden for them
  const canSeeTeam = getScope("lead") !== "own";
  const { success, error } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  useEffect(() => setSearch(searchParams.get("search") || ""), [searchParams]);
  const leadFilters = usePersistentFilters("leads", LEAD_FILTER_DEFAULTS);
  // Filter choices with counts (from the API, within the user's scope)
  const [leadOptions, setLeadOptions] = useState<{
    total: number;
    owners: OwnerFacet[];
    sources: { value: string; count: number }[];
    states: { value: string; count: number }[];
  }>({ total: 0, owners: [], sources: [], states: [] });
  const [actionLoading, setActionLoading] = useState(false);

  // Selection for bulk actions
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());

  // Assignable Reps
  const [assignableReps, setAssignableReps] = useState<Array<{ id: string; first_name: string; last_name?: string; email: string; user_role?: { name: string } }>>([]);

  // Modals
  const [statusLead, setStatusLead] = useState<LeadRecord | null>(null);
  const [deleteLead, setDeleteLead] = useState<LeadRecord | null>(null);
  const [convertLead, setConvertLead] = useState<LeadRecord | null>(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [viewLead, setViewLead] = useState<LeadRecord | null>(null);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);

  // Convert Form
  const [convertForm, setConvertForm] = useState({
    create_deal: true,
    deal_name: "",
    deal_amount: 0,
    expected_close_date: "",
  });

  // Bulk Assign Form
  const [bulkTargetOwner, setBulkTargetOwner] = useState("");
  const [bulkReason, setBulkReason] = useState("BULK_REASSIGN");

  // Single Lead Reassign
  const [reassignLead, setReassignLead] = useState<LeadRecord | null>(null);
  const [reassignOwner, setReassignOwner] = useState("");
  const [reassignReason, setReassignReason] = useState("");

  // CSV Import
  const [importOpen, setImportOpen] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [importRows, setImportRows] = useState<Record<string, any>[]>([]);
  const [importSkipped, setImportSkipped] = useState<Array<{ row: number; reason: string }>>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Status Change State
  const [targetStatus, setTargetStatus] = useState<LeadStatus>("CONTACTED");
  const [disqualifyReason, setDisqualifyReason] = useState<string>("");
  const [disqualifyNotes, setDisqualifyNotes] = useState("");

  const [masterSources, setMasterSources] = useState<MasterLookupRecord[]>([]);
  const [masterDisqualifyReasons, setMasterDisqualifyReasons] = useState<MasterLookupRecord[]>([]);
  const [masterIndustries, setMasterIndustries] = useState<MasterLookupRecord[]>([]);

  const canAssign =
    hasPermission("lead.assign") ||
    user?.UserRole?.RoleCode === "SUPER_ADMIN" ||
    user?.UserRole?.RoleCode === "SALES_HEAD" ||
    (user as any)?.user_role?.code === "SUPER_ADMIN" ||
    (user as any)?.user_role?.code === "SALES_HEAD" ||
    (user as any)?.role === "SUPER_ADMIN" ||
    (user as any)?.role === "SALES_HEAD" ||
    (user as any)?.isSuperAdmin === true;
  const canDelete = hasPermission("lead.delete");
  const canConvert = hasPermission("lead.convert");
  const canImport = hasPermission("lead.import");
  const canUpdateStatus = hasPermission("lead.update");

  /** Opens the status dialog with the next step in the funnel preselected */
  const openStatusChange = (l: LeadRecord) => {
    const next: Partial<Record<LeadStatus, LeadStatus>> = { NEW: "CONTACTED", CONTACTED: "QUALIFIED", DISQUALIFIED: "NEW" };
    setTargetStatus(next[l.lead_status] || l.lead_status);
    setDisqualifyReason("");
    setDisqualifyNotes("");
    setStatusLead(l);
  };

  const list = usePagedList<LeadRecord>({
    deps: [leadFilters.values, search],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const f = leadFilters.values;
      const query = toQuery({
        search: search.trim(),
        owner: f.owner,
        priority: f.priority,
        source: f.source,
        state: f.state,
        ...dateRangeParams(f.created, "created"),
        page,
        page_size: 30,
      });
      try {
        const [res, options] = await Promise.all([
          api.get(`/api/Lead/List${query}`),
          api.get("/api/Lead/FilterOptions").catch(() => null),
        ]);
        if (options) setLeadOptions(unwrapObject(options));
        return unwrapPage<LeadRecord>(res);
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load leads");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const leads = list.rows;
  const loading = list.loading;
  const loadLeads = list.reload;

  const loadAssignableReps = async () => {
    try {
      const res = await api.get("/api/User/Assignable?for=lead");
      setAssignableReps(unwrapArray(res));
    } catch {
      // Non-fatal
    }
  };

  useEffect(() => {
    loadAssignableReps();
    api
      .get("/api/MasterLookup/ByCategory/LEAD_SOURCE")
      .then((res) => setMasterSources(unwrapArray<MasterLookupRecord>(res)))
      .catch(() => {});
    api
      .get("/api/MasterLookup/ByCategory/LEAD_DISQUALIFY_REASON")
      .then((res) => setMasterDisqualifyReasons(unwrapArray<MasterLookupRecord>(res)))
      .catch(() => {});
    api
      .get("/api/MasterLookup/ByCategory/INDUSTRY")
      .then((res) => setMasterIndustries(unwrapArray<MasterLookupRecord>(res)))
      .catch(() => {});
  }, []);

  const handleClaimLead = async (leadId: string) => {
    setActionLoading(true);
    try {
      await api.post(`/api/Lead/Claim/${leadId}`);
      success("Lead Claimed", "Lead has been claimed and assigned to your queue");
      await loadLeads();
    } catch (err: any) {
      error("Claim Failed", err.message || "Could not claim lead");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAssignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkTargetOwner) {
      error("Select Assignee", "Please select a sales representative");
      return;
    }

    setActionLoading(true);
    try {
      const res = await api.post("/api/Lead/BulkAssign", {
        lead_ids: Array.from(selectedLeadIds),
        target_owner_id: bulkTargetOwner,
        reason: bulkReason || "BULK_REASSIGN",
      });
      const result = res || {};
      const count = Number(result.count ?? selectedLeadIds.size);
      const skipped = Number(result.skipped ?? 0);
      success(
        "Leads Reassigned",
        `Reassigned ${count} lead${count === 1 ? "" : "s"}` +
          (skipped ? `; ${skipped} skipped (converted or outside your team)` : ""),
      );
      setBulkAssignOpen(false);
      setSelectedLeadIds(new Set());
      await loadLeads();
    } catch (err: any) {
      error("Bulk Assignment Failed", err.message || "Failed to reassign selected leads");
    } finally {
      setActionLoading(false);
    }
  };

  const openReassign = (l: LeadRecord) => {
    setReassignLead(l);
    setReassignOwner("");
    setReassignReason("");
  };

  const handleReassignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reassignLead) return;
    if (!reassignOwner) {
      error("Select Assignee", "Please select a sales representative");
      return;
    }

    setActionLoading(true);
    try {
      await api.patch(`/api/Lead/Reassign/${reassignLead.id}`, {
        owner_id: reassignOwner,
        reason: reassignReason.trim() || undefined,
      });
      success("Lead Reassigned", `${reassignLead.first_name} ${reassignLead.last_name || ""} has a new owner`);
      setReassignLead(null);
      await loadLeads();
    } catch (err: any) {
      error("Reassignment Failed", err.message || "Failed to reassign lead");
    } finally {
      setActionLoading(false);
    }
  };

  const resetImport = () => {
    setImportFileName("");
    setImportRows([]);
    setImportSkipped([]);
    setImportResult(null);
  };

  const handleImportFile = async (file: File | undefined) => {
    resetImport();
    if (!file) return;
    setImportFileName(file.name);

    try {
      const records = parseCsvToObjects(await file.text());
      const rows: Record<string, any>[] = [];
      const skipped: Array<{ row: number; reason: string }> = [];

      records.forEach((rec, idx) => {
        // Row numbers are 1-based and account for the header line
        const rowNumber = idx + 2;
        if (!rec.first_name || !rec.company_name) {
          skipped.push({ row: rowNumber, reason: "first_name and company_name are required" });
          return;
        }

        const lead: Record<string, any> = { source: "IMPORT" };
        for (const field of IMPORT_TEXT_FIELDS) {
          if (rec[field]) lead[field] = rec[field];
        }
        if (lead.priority) lead.priority = String(lead.priority).toUpperCase();
        if (lead.source) lead.source = String(lead.source).toUpperCase();
        for (const field of IMPORT_NUMBER_FIELDS) {
          const n = Number(rec[field]);
          if (rec[field] && !Number.isNaN(n)) lead[field] = n;
        }
        if (rec.tags) {
          lead.tags = rec.tags.split(";").map((t) => t.trim()).filter(Boolean);
        }
        rows.push(lead);
      });

      setImportRows(rows);
      setImportSkipped(skipped);
      if (records.length === 0) {
        error("Empty File", "No data rows found in the CSV file");
      }
    } catch (err: any) {
      error("Read Failed", err.message || "Could not read the CSV file");
    }
  };

  const handleImportSubmit = async () => {
    if (importRows.length === 0) return;
    setActionLoading(true);
    try {
      const res = await api.post("/api/Lead/Import", importRows);
      const data = (res?.data ?? res) as ImportResult;
      setImportResult(data);
      if (data.successCount > 0) {
        success("Import Complete", `Imported ${data.successCount} of ${data.total} leads`);
        await loadLeads();
      } else {
        error("Import Failed", "No leads were imported. See the row errors below.");
      }
    } catch (err: any) {
      error("Import Failed", err.message || "Failed to import leads");
    } finally {
      setActionLoading(false);
    }
  };

  const downloadImportTemplate = () => {
    const sample = IMPORT_TEMPLATE_HEADER.split(",")
      .map((h) =>
        h === "first_name" ? "Ravi" : h === "company_name" ? "Acme Pvt Ltd" : h === "priority" ? "WARM" : ""
      )
      .join(",");
    const blob = new Blob([`${IMPORT_TEMPLATE_HEADER}\n${sample}\n`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lead-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleStatusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!statusLead) return;

    if (targetStatus === "DISQUALIFIED" && !disqualifyReason.trim()) {
      error("Reason Required", "Please provide a disqualification reason");
      return;
    }

    setActionLoading(true);
    try {
      await api.patch(`/api/Lead/ChangeStatus/${statusLead.id}`, {
        status: targetStatus,
        disqualify_reason: targetStatus === "DISQUALIFIED" ? disqualifyReason : undefined,
        disqualify_notes: targetStatus === "DISQUALIFIED" ? disqualifyNotes : undefined,
      });
      success("Status Updated", `Lead transitioned to ${targetStatus}`);
      setStatusLead(null);
      await loadLeads();
    } catch (err: any) {
      error("Transition Failed", err.message || "Invalid status transition");
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!convertLead) return;

    setActionLoading(true);
    try {
      const res = await api.post(`/api/Convert/Lead/${convertLead.id}`, {
        create_deal: convertForm.create_deal,
        deal_name: convertForm.deal_name.trim() || undefined,
        deal_amount: Number(convertForm.deal_amount) || undefined,
        expected_close_date: convertForm.expected_close_date ? new Date(convertForm.expected_close_date).toISOString() : undefined,
      });

      const data = res?.data || res;
      success(
        "Lead Converted Successfully",
        `Created Account: "${data.account_name || convertLead.company_name}" and Contact.`
      );
      setConvertLead(null);
      await loadLeads();
    } catch (err: any) {
      error("Conversion Failed", err.message || "Could not convert lead");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteLead) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/Lead/Delete/${deleteLead.id}`);
      success("Lead Deleted", "Lead record has been removed");
      setDeleteLead(null);
      await loadLeads();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete lead");
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = async (l: LeadRecord) => {
    setViewDetailsLoading(true);
    setViewLead(l);
    try {
      const res = await api.get(`/api/Lead/ById/${l.id}`);
      const detailed = res?.data || res;
      setViewLead(detailed);
    } catch {
      // Fallback to row data
    } finally {
      setViewDetailsLoading(false);
    }
  };

  const toggleSelectLead = (id: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.size === filteredLeads.length && filteredLeads.length > 0) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(filteredLeads.map((l) => l.id)));
    }
  };

  // The API already applied every filter
  const filteredLeads = leads;

  const renderPriorityBadge = (priority?: LeadPriority, score?: number) => {
    const p = priority || (score && score >= 60 ? "HOT" : score && score < 30 ? "COLD" : "WARM");
    if (p === "HOT") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-500/10 text-rose-600 border border-rose-500/20">
          <Flame className="size-3 text-rose-500 animate-pulse" />
          <span>Hot ({score || 70})</span>
        </span>
      );
    }
    if (p === "COLD") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-500/10 text-slate-500 border border-slate-500/20">
          <Snowflake className="size-3 text-slate-400" />
          <span>Cold ({score || 20})</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/20">
        <Zap className="size-3 text-amber-500" />
        <span>Warm ({score || 45})</span>
      </span>
    );
  };

  const renderFollowUpPill = (followUpDate?: string) => {
    if (!followUpDate) return <span className="text-xs text-muted-foreground italic">None scheduled</span>;
    const target = new Date(followUpDate);
    const now = new Date();
    const isPast = target.getTime() < now.getTime() - 1000 * 60 * 60; // 1 hour grace
    const isToday = target.toDateString() === now.toDateString();

    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border",
          isPast
            ? "bg-rose-500/10 text-rose-600 border-rose-500/20"
            : isToday
            ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
            : "bg-muted/50 text-foreground border-border"
        )}
      >
        <Calendar className="size-3 shrink-0" />
        <span>
          {isPast ? "Overdue: " : isToday ? "Today: " : ""}
          {target.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
        </span>
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Leads
            </h1>
            <Badge variant="outline" className="text-xs font-semibold px-2.5 py-0.5">
              {list.total} Leads
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            Capture incoming inquiries, assign leads to sales representatives, and push opportunities to the Sales Pipeline
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {selectedLeadIds.size > 0 && canAssign && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkAssignOpen(true)}
              className="border-primary/30 text-primary hover:bg-primary/5"
            >
              <Share2 className="size-4 mr-1.5" />
              Bulk Assign ({selectedLeadIds.size})
            </Button>
          )}

          {canImport && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                resetImport();
                setImportOpen(true);
              }}
              className="border-border text-foreground hover:bg-accent"
            >
              <Upload className="size-4 mr-1.5" />
              Import CSV
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={loadLeads}
            disabled={loading}
            className="border-border text-foreground hover:bg-accent"
          >
            <RefreshCw className={cn("size-4 mr-1.5", loading && "animate-spin")} />
            Refresh
          </Button>

          <Button
            size="sm"
            onClick={() => navigate("/leads/new")}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
          >
            <UserPlus className="size-4 mr-1.5" />
            + New Lead
          </Button>
        </div>
      </div>

      {/* Filter and Search Bar (no funnel status filter by design) */}
      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search by name, company, email, phone...",
          onEnter: loadLeads,
        }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(leadOptions.owners, leadOptions.total, user?.Id), defaultValue: "all" },
          { name: "priority", label: "Priority", icon: FilterPriorityIcon, type: "select", options: PRIORITY_OPTIONS, defaultValue: "all" },
          { name: "source", label: "Source", icon: FilterSourceIcon, type: "select", options: valueOptionsFromFacets(leadOptions.sources, "All sources", leadOptions.total, true), defaultValue: "all" },
          { name: "state", label: "State", icon: FilterStateIcon, type: "select", options: valueOptionsFromFacets(leadOptions.states, "All states", leadOptions.total), defaultValue: "all" },
          { name: "created", label: "Created", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={leadFilters.values}
        onChange={(name, value) => leadFilters.set(name as keyof typeof LEAD_FILTER_DEFAULTS, value)}
        onReset={leadFilters.reset}
        shown={list.total}
        total={list.total}
        noun="leads"
      />

      {/* Leads Table */}
      <div className="border border-border rounded-xl bg-card overflow-hidden shadow-sm">
        {loading && leads.length === 0 ? (
          <TableSkeleton rows={7} columns={8} />
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="size-12 rounded-full bg-muted/50 flex items-center justify-center text-muted-foreground mb-3">
              <UserPlus className="size-6" />
            </div>
            <h3 className="text-base font-semibold text-foreground">No leads found</h3>
            <p className="text-xs text-muted-foreground max-w-sm mt-1">
              No leads matched your filter criteria. Try clearing filters or create a new lead.
            </p>
            <Button
              size="sm"
              onClick={() => navigate("/leads/new")}
              className="mt-4"
            >
              + Create Lead
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40 border-b border-border">
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    checked={selectedLeadIds.size === filteredLeads.length && filteredLeads.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-border size-4 accent-primary cursor-pointer"
                  />
                </TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Prospect & Contact
                </TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Company & Location
                </TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Assigned User
                </TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Requirement / Source
                </TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Status
                </TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Priority
                </TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Follow-up
                </TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-wider text-muted-foreground pr-4">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.map((l) => {
                const isSelected = selectedLeadIds.has(l.id);
                const isUnassigned = !l.owner_id;

                return (
                  <TableRow
                    key={l.id}
                    className={cn(
                      "hover:bg-muted/30 transition-colors border-b border-border/60",
                      isSelected && "bg-primary/5 hover:bg-primary/10"
                    )}
                  >
                    {/* Checkbox */}
                    <TableCell className="w-10">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectLead(l.id)}
                        className="rounded border-border size-4 accent-primary cursor-pointer"
                      />
                    </TableCell>

                    {/* Person */}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="size-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shrink-0">
                          {l.first_name?.[0]?.toUpperCase() || "L"}
                        </div>
                        <div className="min-w-0">
                          <Link
                            to={`/leads/${l.id}`}
                            className="font-semibold text-sm text-foreground hover:text-primary transition-colors text-left flex items-center gap-1.5 hover:underline"
                          >
                            <span>{l.first_name} {l.last_name || ""}</span>
                            {l.job_title && (
                              <span className="text-[10px] text-muted-foreground font-normal bg-muted px-1.5 py-0.5 rounded">
                                {l.job_title}
                              </span>
                            )}
                          </Link>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                            {l.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="size-3 text-muted-foreground" />
                                <span className="truncate max-w-[140px]">{l.email}</span>
                              </span>
                            )}
                            {(l.phone || l.mobile) && (
                              <span className="flex items-center gap-1">
                                <Phone className="size-3 text-muted-foreground" />
                                <span>{l.phone || l.mobile}</span>
                                <WhatsAppButton
                                  phone={l.mobile || l.phone}
                                  name={`${l.first_name} ${l.last_name || ""}`.trim()}
                                  leadId={l.id}
                                />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    {/* Company */}
                    <TableCell>
                      <div className="min-w-0">
                        <Link
                          to={`/leads/${l.id}`}
                          className="text-xs font-semibold text-foreground hover:text-primary hover:underline truncate block"
                        >
                          {l.company_name}
                        </Link>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                          {l.city && (
                            <span className="flex items-center gap-0.5">
                              <MapPin className="size-3" />
                              <span>{l.city}</span>
                            </span>
                          )}
                          {l.industry && <span>• {l.industry}</span>}
                        </div>
                      </div>
                    </TableCell>

                    {/* Assigned User */}
                    <TableCell>
                      {isUnassigned ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/20">
                            Unassigned
                          </span>
                          {canAssign && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReassign(l)}
                              className="h-6 px-2 text-[10px] bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                              Assign User
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2 max-w-[180px]">
                          <div className="flex items-center gap-2 text-xs min-w-0">
                            <div className="size-6 rounded-full bg-sidebar-primary/20 text-sidebar-primary flex items-center justify-center font-bold text-[10px] shrink-0">
                              {l.owner?.first_name?.[0] || "U"}
                            </div>
                            <span className="font-medium text-foreground truncate">
                              {l.owner ? `${l.owner.first_name} ${l.owner.last_name || ""}` : "Assigned"}
                            </span>
                          </div>
                          {canAssign && (
                            <button
                              type="button"
                              onClick={() => openReassign(l)}
                              className="text-[10px] text-primary hover:underline font-medium shrink-0 ml-1"
                            >
                              Change
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>

                    {/* Requirement / Source */}
                    <TableCell className="max-w-[200px]">
                      <div className="text-xs text-foreground truncate font-medium">
                        {l.product_interest || l.requirement_notes || "No details"}
                      </div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Tag className="size-2.5" />
                        <span>Source: {l.source || "Direct"}</span>
                        {l.inquiry_count && l.inquiry_count > 1 ? (
                          <span className="inline-flex items-center text-[9px] bg-blue-500/15 text-blue-600 dark:text-blue-400 font-semibold px-1 py-0.2 rounded" title={`${l.inquiry_count} inquiries received from this contact`}>
                            {l.inquiry_count}x
                          </span>
                        ) : null}
                      </div>
                    </TableCell>

                    {/* Lifecycle status: click to move the lead along (converted leads are final) */}
                    <TableCell>
                      {(() => {
                        const s = STATUS_CONFIG[l.lead_status] || STATUS_CONFIG.NEW;
                        const badge = `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${s.bg} ${s.text}`;
                        return canUpdateStatus && l.lead_status !== "CONVERTED" ? (
                          <button
                            type="button"
                            onClick={() => openStatusChange(l)}
                            className={`${badge} hover:opacity-80 cursor-pointer`}
                            title="Change status"
                          >
                            {s.label}
                            <ChevronDown className="size-3" />
                          </button>
                        ) : (
                          <span className={badge}>{s.label}</span>
                        );
                      })()}
                    </TableCell>

                    {/* Priority & Score */}
                    <TableCell>
                      {renderPriorityBadge(l.priority, l.score)}
                    </TableCell>

                    {/* Next Follow Up */}
                    <TableCell>
                      {renderFollowUpPill(l.next_follow_up_at)}
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right pr-4">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Push to Sales Pipeline: only qualified leads convert (New → Contacted → Qualified → Converted) */}
                        {canConvert && l.lead_status === "QUALIFIED" && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setConvertLead(l);
                              setConvertForm({
                                create_deal: true,
                                deal_name: `${l.first_name} ${l.last_name || ""} - ${l.company_name}`.trim(),
                                deal_amount: Number(l.estimated_value || l.expected_budget || 10000),
                                expected_close_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
                              });
                            }}
                            className="h-7 px-2.5 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs gap-1"
                            title="Push directly into Sales Pipeline"
                          >
                            <Briefcase className="size-3" />
                            <span>Push to Pipeline</span>
                          </Button>
                        )}

                        {/* Edit */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/leads/${l.id}/edit`)}
                          title="Edit Lead"
                          className="size-7 text-muted-foreground hover:text-foreground"
                        >
                          <Edit className="size-3.5" />
                        </Button>

                        {/* Assign Lead */}
                        {canAssign && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openReassign(l)}
                            title="Assign to Sales Rep"
                            className="size-7 text-muted-foreground hover:text-foreground"
                          >
                            <Share2 className="size-3.5" />
                          </Button>
                        )}

                        {/* Delete */}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteLead(l)}
                            title="Delete Lead"
                            className="size-7 text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
      </div>

      {/* STATUS CHANGE / DISQUALIFY DIALOG */}
      <Dialog open={!!statusLead} onOpenChange={(open) => !open && setStatusLead(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <UserCheck className="size-5 text-primary" />
              <span>Update Lifecycle Status</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Transition lead "{statusLead?.company_name}" across sales funnel stages.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleStatusSubmit} className="space-y-4 text-xs sm:text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Target Stage</Label>
              <select
                value={targetStatus}
                onChange={(e) => setTargetStatus(e.target.value as LeadStatus)}
                className="w-full h-10 sm:h-11 rounded-lg border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm font-medium"
              >
                <option value="NEW">New</option>
                <option value="CONTACTED">Contacted</option>
                <option value="QUALIFIED">Qualified</option>
                <option value="DISQUALIFIED">Disqualified</option>
              </select>
            </div>

            {targetStatus === "DISQUALIFIED" && (
              <div className="space-y-3 bg-destructive/5 p-3.5 rounded-lg border border-destructive/20">
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-semibold text-destructive">
                    Disqualification Reason *
                  </Label>
                  <select
                    value={disqualifyReason}
                    onChange={(e) => setDisqualifyReason(e.target.value)}
                    className="w-full h-10 sm:h-11 rounded-lg border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm"
                    required
                  >
                    <option value="">-- Select Disqualification Reason --</option>
                    {masterDisqualifyReasons.map((r) => (
                      <option key={r.code} value={r.name}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-semibold text-destructive">
                    Reason Notes
                  </Label>
                  <textarea
                    rows={3}
                    placeholder="Specific explanation on why this lead is not viable..."
                    value={disqualifyNotes}
                    onChange={(e) => setDisqualifyNotes(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background p-3 text-xs sm:text-sm"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setStatusLead(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Updating..." : "Confirm Transition"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CONVERT LEAD DIALOG */}
      <Dialog open={!!convertLead} onOpenChange={(open) => !open && setConvertLead(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2 text-emerald-600">
              <ArrowRightCircle className="size-5" />
              <span>Convert Lead to Opportunity</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Qualify prospect into permanent CRM records.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleConvertSubmit} className="space-y-3.5 text-xs sm:text-sm">
            <div className="p-3 bg-muted/40 rounded-lg space-y-2 border border-border text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <Building className="size-4 text-primary" />
                <span>Account created: <strong>{convertLead?.company_name}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <User className="size-4 text-primary" />
                <span>Contact created: <strong>{convertLead?.first_name} {convertLead?.last_name || ""}</strong></span>
              </div>
            </div>

            <div className="pt-1 space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Deal Opportunity Name</Label>
              <Input
                required
                value={convertForm.deal_name}
                onChange={(e) => setConvertForm({ ...convertForm, deal_name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Deal Amount (₹)</Label>
                <Input
                  type="number"
                  value={convertForm.deal_amount}
                  onChange={(e) => setConvertForm({ ...convertForm, deal_amount: Number(e.target.value) })}
                  className="mt-1"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Expected Close Date</Label>
                <Input
                  type="date"
                  value={convertForm.expected_close_date}
                  onChange={(e) => setConvertForm({ ...convertForm, expected_close_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" size="sm" onClick={() => setConvertLead(null)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={actionLoading}>
                {actionLoading ? "Converting..." : "Complete Conversion"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* BULK ASSIGN DIALOG */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <Share2 className="size-5 text-primary" />
              <span>Bulk Reassign Leads</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Transfer {selectedLeadIds.size} selected leads to a designated sales representative.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleBulkAssignSubmit} className="space-y-4 text-xs sm:text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Select Destination Owner *</Label>
              <select
                required
                value={bulkTargetOwner}
                onChange={(e) => setBulkTargetOwner(e.target.value)}
                className="w-full h-10 sm:h-11 rounded-lg border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm font-medium"
              >
                <option value="">-- Choose Sales Representative --</option>
                {assignableReps.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.first_name} {r.last_name || ""} — {r.user_role?.name || "Representative"} ({r.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Reassignment Reason / Note</Label>
              <Input
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="e.g. Territory realignment, rep leave transfer"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setBulkAssignOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Reassigning..." : `Reassign ${selectedLeadIds.size} Leads`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SINGLE LEAD REASSIGN / ASSIGN DIALOG */}
      <Dialog open={!!reassignLead} onOpenChange={(open) => !open && setReassignLead(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <Share2 className="size-5 text-primary" />
              <span>{reassignLead?.owner ? "Reassign Lead to User" : "Assign Lead to User"}</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              {reassignLead?.first_name} {reassignLead?.last_name || ""} ({reassignLead?.company_name}) is
              currently{" "}
              {reassignLead?.owner ? (
                <>
                  assigned to <strong className="text-foreground">{reassignLead.owner.first_name} {reassignLead.owner.last_name || ""}</strong>.
                </>
              ) : (
                <strong className="text-amber-500 font-semibold">unassigned (in pool)</strong>
              )}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleReassignSubmit} className="space-y-4 text-xs sm:text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Select Sales Representative *</Label>
              <select
                required
                value={reassignOwner}
                onChange={(e) => setReassignOwner(e.target.value)}
                className="w-full h-10 sm:h-11 rounded-lg border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm font-medium"
              >
                <option value="">-- Choose Sales Representative --</option>
                {assignableReps
                  .filter((r) => r.id !== reassignLead?.owner_id)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.first_name} {r.last_name || ""} — {r.user_role?.name || "Representative"} ({r.email})
                    </option>
                  ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Assignment Reason / Note</Label>
              <Input
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="e.g. Lead allocation, rep follow-up assignment"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setReassignLead(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading} className="bg-primary hover:bg-primary/90">
                {actionLoading ? "Assigning..." : reassignLead?.owner ? "Confirm Reassignment" : "Confirm Assignment"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV IMPORT DIALOG */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl bg-card border-border text-foreground max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold flex items-center gap-2">
              <FileSpreadsheet className="size-5 text-primary" />
              <span>Import Leads from CSV</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              The first row must be a header. <code>first_name</code> and <code>company_name</code> are
              required; separate multiple tags with <code>;</code>. Duplicate checks and auto-assignment
              rules apply to every row.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 text-xs sm:text-sm">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <label className="flex-1 flex items-center gap-2 h-9 sm:h-10 px-3 sm:px-3.5 rounded-md border border-dashed border-border bg-background text-xs sm:text-sm cursor-pointer hover:bg-accent">
                <Upload className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{importFileName || "Choose a .csv file"}</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    handleImportFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </label>
              <Button type="button" variant="ghost" size="sm" onClick={downloadImportTemplate} className="text-xs sm:text-sm self-start sm:self-auto">
                Download template
              </Button>
            </div>

            {importFileName && !importResult && (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs sm:text-sm space-y-1">
                <div>
                  <strong className="text-foreground">{importRows.length}</strong> row(s) ready to import
                </div>
                {importSkipped.length > 0 && (
                  <div className="text-amber-600">
                    {importSkipped.length} row(s) skipped:{" "}
                    {importSkipped
                      .slice(0, 5)
                      .map((s) => `row ${s.row}`)
                      .join(", ")}
                    {importSkipped.length > 5 ? "…" : ""} (missing first_name or company_name)
                  </div>
                )}
              </div>
            )}

            {importResult && (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs sm:text-sm space-y-2">
                <div className="flex items-center gap-1.5 text-emerald-600 font-semibold">
                  <CheckCircle className="size-3.5" />
                  Imported {importResult.successCount} of {importResult.total} leads
                </div>
                {importResult.errors.length > 0 && (
                  <div className="space-y-1">
                    <div className="font-semibold text-destructive">
                      {importResult.errors.length} row(s) failed:
                    </div>
                    <ul className="max-h-40 overflow-y-auto space-y-0.5 text-muted-foreground">
                      {importResult.errors.map((e) => (
                        <li key={e.row}>
                          Row {e.row}: {e.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setImportOpen(false)}>
              {importResult ? "Close" : "Cancel"}
            </Button>
            {!importResult && (
              <Button
                type="button"
                size="sm"
                onClick={handleImportSubmit}
                disabled={actionLoading || importRows.length === 0}
              >
                {actionLoading ? "Importing..." : `Import ${importRows.length} Leads`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW DETAILS / AUDIT DRAWER */}
      <Dialog open={!!viewLead} onOpenChange={(open) => !open && setViewLead(null)}>
        <DialogContent className="sm:max-w-3xl md:max-w-4xl bg-card border-border text-foreground max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <span>{viewLead?.company_name}</span>
                {renderPriorityBadge(viewLead?.priority, viewLead?.score)}
              </DialogTitle>
            </div>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Contact: {viewLead?.first_name} {viewLead?.last_name || ""} • Designation: {viewLead?.job_title || "N/A"}
            </DialogDescription>
          </DialogHeader>

          {viewDetailsLoading ? (
            <div className="p-8 text-center text-xs sm:text-sm text-muted-foreground">Loading audit dossier...</div>
          ) : (
            <div className="space-y-4 pt-1 text-xs sm:text-sm">
              {/* Contact & Commercial Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="p-3.5 bg-muted/30 rounded-lg border border-border space-y-1.5">
                  <div className="font-semibold text-foreground">Contact Channels</div>
                  <div>Email: {viewLead?.email || "—"}</div>
                  <div>Phone: {viewLead?.phone || "—"}</div>
                  <div>Mobile: {viewLead?.mobile || "—"}</div>
                  {viewLead && (viewLead.mobile || viewLead.phone) && (
                    <WhatsAppButton
                      variant="label"
                      phone={viewLead.mobile || viewLead.phone}
                      name={`${viewLead.first_name} ${viewLead.last_name || ""}`.trim()}
                      leadId={viewLead.id}
                    />
                  )}
                  <div>City/State: {[viewLead?.city, viewLead?.state].filter(Boolean).join(", ") || "—"}</div>
                </div>

                <div className="p-3.5 bg-muted/30 rounded-lg border border-border space-y-1.5">
                  <div className="font-semibold text-foreground">Commercial Scope</div>
                  <div>Product Interest: {viewLead?.product_interest || "—"}</div>
                  <div>Budget: {viewLead?.expected_budget ? formatINR(viewLead.expected_budget) : "—"}</div>
                  <div>Timeline: {viewLead?.expected_timeline || "—"}</div>
                  <div>Source: {viewLead?.source || "—"}</div>
                </div>
              </div>

              {viewLead?.requirement_notes && (
                <div className="p-3.5 bg-muted/30 rounded-lg border border-border">
                  <div className="font-semibold text-foreground mb-1">Requirement Brief</div>
                  <p className="text-muted-foreground whitespace-pre-wrap">{viewLead.requirement_notes}</p>
                </div>
              )}

              {/* Tasks & Follow-up Reminders */}
              {viewLead && (
                <LeadTasksSection
                  leadId={viewLead.id}
                  leadName={[viewLead.first_name, viewLead.last_name].filter(Boolean).join(" ") || viewLead.company_name}
                  onFollowUpChanged={loadLeads}
                />
              )}

              {/* Assignment Audit Trail */}
              <div>
                <div className="font-semibold text-sm sm:text-base text-foreground mb-2 flex items-center gap-1.5">
                  <History className="size-4 text-primary" />
                  <span>Assignment & Lifecycle History</span>
                </div>

                {!viewLead?.assignment_history || viewLead.assignment_history.length === 0 ? (
                  <div className="p-3 bg-muted/20 rounded border border-border text-muted-foreground italic">
                    Original assignment to current owner.
                  </div>
                ) : (
                  <div className="space-y-2 border-l-2 border-border ml-2 pl-3">
                    {viewLead.assignment_history.map((hist) => (
                      <div key={hist.id} className="relative pb-2">
                        <div className="absolute -left-[19px] top-1 size-2 rounded-full bg-primary" />
                        <div className="font-medium text-foreground">
                          {hist.reason.replace(/_/g, " ")}: Transferred to{" "}
                          <strong>
                            {hist.to_user ? `${hist.to_user.first_name} ${hist.to_user.last_name || ""}` : "Unassigned Pool"}
                          </strong>
                        </div>
                        <div className="text-[11px] sm:text-xs text-muted-foreground">
                          By {hist.assigned_by ? `${hist.assigned_by.first_name}` : "System"} on{" "}
                          {new Date(hist.created_on).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="pt-2 border-t border-border">
            <Button size="sm" variant="outline" onClick={() => setViewLead(null)}>
              Close Dossier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={!!deleteLead} onOpenChange={(open) => !open && setDeleteLead(null)}>
        <DialogContent className="sm:max-w-lg bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold text-destructive">
              Delete Lead Record?
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Are you sure you want to delete the lead for "{deleteLead?.company_name}"? This action can only be taken by an administrator.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteLead(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LeadsPage;
